import React, { useState, useEffect, useRef } from 'react';
import Modal from '../common/Modal';
import api from '../../services/api';
import { exportToCsv } from '../../utils/exportCsv';
import { formatMoney } from '../../utils/currency';
import { toast } from 'react-toastify';
import { MagnifyingGlassIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { dateInputToBusinessUTC, todayBusinessIso } from '../../utils/datetime';

/**
 * RecordPayoutDialog
 * Inline modal for the central /payouts page payroll flow:
 *   1. Search a driver by name / phone
 *   2. View their balance + download their earnings sheet
 *   3. Enter amount, bank reference, optional date + note
 *   4. POST /admin/drivers/:id/payouts → onRecorded() callback
 *
 * Props:
 *   isOpen       {boolean}
 *   onClose      {function}
 *   onRecorded   {function}  called after successful payout — refresh the list
 *   currencyCode {string}    default 'QAR'
 */
const RecordPayoutDialog = ({ isOpen, onClose, onRecorded, currencyCode = 'QAR' }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [amount, setAmount] = useState('');
  const [bankReference, setBankReference] = useState('');
  const [date, setDate] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [earningsDownloading, setEarningsDownloading] = useState(false);
  const [error, setError] = useState('');
  const debounceRef = useRef(null);

  // Reset all state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSearchResults([]);
      setSelectedDriver(null);
      setAmount('');
      setBankReference('');
      setDate('');
      setNote('');
      setError('');
      setSubmitting(false);
      setEarningsDownloading(false);
    }
  }, [isOpen]);

  // Debounced driver search
  useEffect(() => {
    if (!searchQuery.trim() || selectedDriver) {
      setSearchResults([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get('/admin/payouts/driver-search', {
          params: { search: searchQuery.trim() },
        });
        setSearchResults(res.data.data || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery, selectedDriver]);

  const selectDriver = (driver) => {
    setSelectedDriver(driver);
    setSearchQuery(`${driver.firstName} ${driver.lastName}`);
    setSearchResults([]);
    setAmount(driver.ledgerBalance > 0 ? String(driver.ledgerBalance) : '');
    setError('');
  };

  const clearDriver = () => {
    setSelectedDriver(null);
    setSearchQuery('');
    setSearchResults([]);
    setAmount('');
    setError('');
  };

  const handleDownloadEarnings = async () => {
    if (!selectedDriver) return;
    setEarningsDownloading(true);
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      await exportToCsv(
        '/admin/exports/driver-earnings',
        { driverId: selectedDriver._id },
        `driver-earnings-${selectedDriver._id}-${stamp}.csv`
      );
    } catch {
      // exportToCsv handles its own error toast
    } finally {
      setEarningsDownloading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const parsedAmount = Number(amount);
    if (!parsedAmount || parsedAmount <= 0) {
      setError('Amount must be a positive number');
      return;
    }
    if (!bankReference.trim()) {
      setError('Bank reference is required');
      return;
    }
    if (parsedAmount > (selectedDriver?.ledgerBalance || 0)) {
      setError(
        `Amount exceeds available balance (${formatMoney(selectedDriver.ledgerBalance, currencyCode)})`
      );
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/admin/drivers/${selectedDriver._id}/payouts`, {
        amount: parsedAmount,
        bankReference: bankReference.trim(),
        date: dateInputToBusinessUTC(date, 'start') || undefined,
        note: note.trim() || undefined,
      });
      toast.success('Payout recorded successfully');
      onRecorded();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to record payout');
    } finally {
      setSubmitting(false);
    }
  };

  // Settlement max anchored to Qatar business timezone so the cutoff is consistent worldwide
  const todayIso = todayBusinessIso();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Record Payout"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="record-payout-form"
            disabled={!selectedDriver || submitting}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Recording…' : 'Record Payout'}
          </button>
        </>
      }
    >
      <form id="record-payout-form" onSubmit={handleSubmit} className="space-y-4">

        {/* Driver search */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Driver</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="w-4 h-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (selectedDriver) clearDriver();
              }}
              placeholder="Search by name or phone…"
              autoComplete="off"
              className="block w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            {searching && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <div className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>

          {/* Dropdown results */}
          {searchResults.length > 0 && (
            <ul className="mt-1 border border-gray-200 rounded-lg shadow-lg bg-white max-h-44 overflow-auto divide-y divide-gray-100 relative z-10">
              {searchResults.map((d) => (
                <li key={d._id}>
                  <button
                    type="button"
                    onClick={() => selectDriver(d)}
                    className="w-full text-left px-3 py-2.5 hover:bg-indigo-50 transition-colors"
                  >
                    <span className="text-sm font-medium text-gray-800">
                      {d.firstName} {d.lastName}
                    </span>
                    <span className="text-xs text-gray-500 ml-2">{d.phone}</span>
                    <span
                      className={`text-xs font-medium ml-2 ${
                        d.ledgerBalance > 0 ? 'text-emerald-600' : 'text-gray-400'
                      }`}
                    >
                      {formatMoney(d.ledgerBalance, currencyCode)}
                    </span>
                    {!d.hasBankAccount && (
                      <span className="ml-2 text-xs text-amber-600">no IBAN</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {searchQuery.trim().length >= 1 && !searching && !selectedDriver && searchResults.length === 0 && (
            <p className="mt-1 text-xs text-gray-400 pl-1">No drivers found</p>
          )}
        </div>

        {/* Selected driver: balance + earnings download */}
        {selectedDriver && (
          <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2.5 border border-gray-200">
            <div>
              <p className="text-xs text-gray-500">Available balance</p>
              <p
                className={`text-lg font-semibold ${
                  selectedDriver.ledgerBalance > 0 ? 'text-gray-800' : 'text-gray-400'
                }`}
              >
                {formatMoney(selectedDriver.ledgerBalance, currencyCode)}
              </p>
            </div>
            <button
              type="button"
              onClick={handleDownloadEarnings}
              disabled={earningsDownloading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 disabled:opacity-50 transition-colors"
            >
              <ArrowDownTrayIcon className="w-3.5 h-3.5" />
              {earningsDownloading ? 'Downloading…' : 'Earnings Sheet'}
            </button>
          </div>
        )}

        {/* Amount */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Amount ({currencyCode}) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            required
            disabled={!selectedDriver}
            className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        </div>

        {/* Bank Reference */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Bank Reference / Transfer ID <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={bankReference}
            onChange={(e) => setBankReference(e.target.value)}
            placeholder="e.g. TXN-20240615-001"
            required
            disabled={!selectedDriver}
            className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        </div>

        {/* Transfer date (optional) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Transfer Date{' '}
            <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            max={todayIso}
            disabled={!selectedDriver}
            className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        </div>

        {/* Note (optional) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Note{' '}
            <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Payroll cycle, batch ref, etc."
            disabled={!selectedDriver}
            className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        </div>

        {/* Inline error */}
        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
};

export default RecordPayoutDialog;
